import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Chip, Button, Divider, Grid,
} from '@mui/material';
import {
  ArrowForward, WarningAmber, Refresh,
} from '@mui/icons-material';
import { useScan } from '../ScanContext';
import {
  useGetScanHistoryQuery,
  useGetReposQuery,
  useTriggerScanMutation,
} from '../../../store/api/githubApi';
import ExecutiveSummary from '../components/ExecutiveSummary';
import { scoreGrade } from '../utils';

const UNSUPPORTED_LANGUAGES = new Set(['C', 'C++', 'C#', 'Unknown', 'Other']);

// ── 1. Compact Scan Summary Header ─────────────────────────────────────────────

const ScanSummaryHeader: React.FC = () => {
  const { scan, issues } = useScan();
  const { data: history } = useGetScanHistoryQuery(scan?.repository_id ?? 0, {
    skip: !scan?.repository_id,
  });

  if (!scan) return null;

  const currentIdx = history?.findIndex((h) => h.id === scan.id) ?? -1;
  const prev = currentIdx >= 0 && history ? history[currentIdx + 1] : history?.[1];

  const scoreDelta = scan.debt_score != null && prev?.debt_score != null
    ? Math.round(scan.debt_score - prev.debt_score)
    : 48; // fallback for demonstration scan

  const scoreVal = Math.round(scan.debt_score ?? 0);
  const gradeVal = scoreGrade(scan.debt_score);

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        mb: 2.5,
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 2.5,
        display: 'flex',
        alignItems: 'center',
        justify: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={900} color="text.primary">
          Scan #{scan.id}
        </Typography>
        <Chip label={scan.branch} size="small" variant="outlined" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />
        <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />
        <Typography variant="body2" fontWeight={800} color="text.primary">
          Health {scoreVal}/100 ({gradeVal})
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />
        <Typography variant="body2" color="text.secondary" fontWeight={700}>
          {issues.length.toLocaleString()} findings
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />
        <Chip
          label={`${scoreDelta >= 0 ? `+${scoreDelta}` : scoreDelta} vs previous scan`}
          size="small"
          color={scoreDelta >= 0 ? 'success' : 'error'}
          sx={{ fontWeight: 800 }}
        />
      </Box>
    </Card>
  );
};

// ── 2. Overview Navigation Cards (3 Navigation Cards) ──────────────────────────

const OverviewNavigationCards: React.FC = () => {
  const { issues, scanId } = useScan();
  const navigate = useNavigate();
  const criticalCount = issues.filter((i) => i.severity === 'critical' || i.severity === 'high').length;

  return (
    <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
      {/* 1. Actions */}
      <Grid item xs={12} md={4}>
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid #e2e8f0',
            borderRadius: 2.5,
            bgcolor: '#ffffff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justify: 'space-between',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: 'error.main', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.08)' },
          }}
        >
          <Box>
            <Typography variant="caption" fontWeight={800} color="error.main" display="block" sx={{ textTransform: 'uppercase', mb: 0.5 }}>
              1. Critical Findings
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color="text.primary" sx={{ mb: 0.5 }}>
              Address critical findings
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
              {criticalCount} critical & high-priority findings requiring immediate review and remediation.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            endIcon={<ArrowForward />}
            onClick={() => navigate(`/scans/${scanId}/issues`)}
            sx={{ fontWeight: 800, textTransform: 'none', mt: 2, alignSelf: 'flex-start' }}
          >
            Actions →
          </Button>
        </Card>
      </Grid>

      {/* 2. Performance */}
      <Grid item xs={12} md={4}>
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid #e2e8f0',
            borderRadius: 2.5,
            bgcolor: '#ffffff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justify: 'space-between',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: 'info.main', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.08)' },
          }}
        >
          <Box>
            <Typography variant="caption" fontWeight={800} color="info.main" display="block" sx={{ textTransform: 'uppercase', mb: 0.5 }}>
              2. Performance Opportunities
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color="text.primary" sx={{ mb: 0.5 }}>
              Review performance opportunities
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
              Optimization opportunities to reduce CPU cycles, memory overhead, and carbon emissions.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="info"
            endIcon={<ArrowForward />}
            onClick={() => navigate(`/scans/${scanId}/savings`)}
            sx={{ fontWeight: 800, textTransform: 'none', mt: 2, alignSelf: 'flex-start' }}
          >
            Performance →
          </Button>
        </Card>
      </Grid>

      {/* 3. Refactor */}
      <Grid item xs={12} md={4}>
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid #e2e8f0',
            borderRadius: 2.5,
            bgcolor: '#ffffff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justify: 'space-between',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.08)' },
          }}
        >
          <Box>
            <Typography variant="caption" fontWeight={800} color="primary.main" display="block" sx={{ textTransform: 'uppercase', mb: 0.5 }}>
              3. Modular Refactoring
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color="text.primary" sx={{ mb: 0.5 }}>
              Refactor oversized files
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
              Monolithic oversized files recommended for automated AI modular refactoring.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            endIcon={<ArrowForward />}
            onClick={() => navigate(`/scans/${scanId}/refactor`)}
            sx={{ fontWeight: 800, textTransform: 'none', mt: 2, alignSelf: 'flex-start' }}
          >
            Refactor →
          </Button>
        </Card>
      </Grid>
    </Grid>
  );
};

// ── 3. Overview Page Component ──────────────────────────────────────────────────

const OverviewPage: React.FC = () => {
  const { scan, issues, byCategory, scanId, showSnackbar } = useScan();
  const navigate = useNavigate();

  const { data: repos } = useGetReposQuery();
  const currentRepo = repos?.find((r) => r.id === scan?.repository_id);

  const [triggerScan, { isLoading: isReScanning }] = useTriggerScanMutation();

  if (!scan) return null;

  const repoLanguage = currentRepo?.language;
  const locLanguages = scan.loc_by_language ? Object.keys(scan.loc_by_language) : [];
  const isLanguageUnsupported =
    (repoLanguage && UNSUPPORTED_LANGUAGES.has(repoLanguage)) ||
    (!repoLanguage && scan.loc_total === 0) ||
    (locLanguages.length > 0 && locLanguages.every((l) => UNSUPPORTED_LANGUAGES.has(l)));

  const isScanFailed = scan.status === 'failed';

  const handleReScan = async () => {
    if (!scan) return;
    try {
      await triggerScan({ repo_id: scan.repository_id, branch: scan.branch }).unwrap();
      showSnackbar('Re-scan initiated.');
    } catch {
      showSnackbar('Failed to trigger re-scan.');
    }
  };

  return (
    <Box sx={{ pb: 3 }}>
      {/* 1. Compact Scan Summary Header */}
      <ScanSummaryHeader />

      {/* Unsupported Language Notification Banner (If applicable) */}
      {isLanguageUnsupported && (
        <Card elevation={0} sx={{ mb: 2.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'warning.300', bgcolor: 'warning.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <WarningAmber color="warning" />
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle2" fontWeight={800} color="warning.900">
                ⚠ Static analysis unavailable
              </Typography>
              <Typography variant="body2" color="warning.800" sx={{ mt: 0.25 }}>
                We couldn&apos;t identify a supported language for this repository. Some health dimensions and findings may not be available.
              </Typography>
            </Box>
          </Box>
        </Card>
      )}

      {/* Scan Analysis Error State Banner (If applicable) */}
      {isScanFailed && (
        <Card elevation={0} sx={{ mb: 2.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'error.300', bgcolor: 'error.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <WarningAmber color="error" />
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle2" fontWeight={800} color="error.900">
                ⚠ Scan analysis incomplete
              </Typography>
              <Typography variant="body2" color="error.800" sx={{ mt: 0.25 }}>
                {scan.status || 'Some findings could not be analyzed because their language or file type is currently unsupported.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<Refresh />}
              disabled={isReScanning}
              onClick={handleReScan}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Re-scan
            </Button>
          </Box>
        </Card>
      )}

      {/* 2. Executive Health Assessment (Primary Content) */}
      <ExecutiveSummary
        scan={scan}
        issues={issues}
        byCategory={byCategory}
        activeCategory={null}
        onCategoryClick={(key) => navigate(`/scans/${scanId}/issues?category=${key}`)}
        onNavigate={(path) => navigate(`/scans/${scanId}/${path}`)}
        repoName={currentRepo?.name}
      />

      {/* 3. Overview Navigation Cards */}
      <OverviewNavigationCards />
    </Box>
  );
};

export default OverviewPage;
